import { Controller } from "@hotwired/stimulus"

// Collapsible rich-text container.
// Shows a truncated preview by default (max-height), with a "Show more" / "Show less" toggle.
// Only renders the toggle button when the content actually overflows.
// Persists expanded/collapsed state to localStorage when a storage-key is provided.
//
// Usage:
//   <div data-controller="expandable-text"
//        data-expandable-text-storage-key-value="card_42_desc">
//     <div data-expandable-text-target="content">...</div>
//     <button data-action="click->expandable-text#toggle"
//             data-expandable-text-target="toggle" class="hidden">Show more</button>
//   </div>
export default class extends Controller {
  static targets = ["content", "toggle", "gradient"]
  static values = {
    storageKey: { type: String, default: "" }
  }

  connect() {
    requestAnimationFrame(() => this._init())
  }

  _init() {
    const content = this.contentTarget
    const naturalHeight = content.scrollHeight
    const collapsedHeight = this._collapsedHeight()

    if (naturalHeight <= collapsedHeight) {
      // Content fits — no toggle needed
      return
    }

    // Show the toggle button
    if (this.hasToggleTarget) this.toggleTarget.classList.remove("hidden")
    if (this.hasGradientTarget) this.gradientTarget.classList.remove("hidden")

    const expanded = this._readStorage()
    if (expanded) {
      this._applyExpanded()
    } else {
      this._applyCollapsed()
    }
  }

  toggle() {
    const isCollapsed = this.contentTarget.style.maxHeight !== "none" &&
                        this.contentTarget.style.maxHeight !== ""
    if (isCollapsed) {
      this._applyExpanded()
    } else {
      this._applyCollapsed()
    }
  }

  _applyExpanded() {
    this.contentTarget.style.maxHeight = "none"
    this.contentTarget.style.overflow = "visible"
    if (this.hasToggleTarget) this.toggleTarget.textContent = "Show less"
    if (this.hasGradientTarget) this.gradientTarget.classList.add("hidden")
    this._saveStorage(true)
  }

  _applyCollapsed() {
    this.contentTarget.style.maxHeight = `${this._collapsedHeight()}px`
    this.contentTarget.style.overflow = "hidden"
    if (this.hasToggleTarget) this.toggleTarget.textContent = "Show more"
    if (this.hasGradientTarget) this.gradientTarget.classList.remove("hidden")
    this._saveStorage(false)
  }

  _collapsedHeight() {
    // ~4 lines at 1.5 line-height * 14px base = ~84px. Use 96px for breathing room.
    return 96
  }

  _readStorage() {
    if (!this.storageKeyValue) return false
    return localStorage.getItem(this.storageKeyValue) === "true"
  }

  _saveStorage(expanded) {
    if (!this.storageKeyValue) return
    localStorage.setItem(this.storageKeyValue, expanded ? "true" : "false")
  }
}
